import { Accordion, PriceIcon } from "@au-schein-de/au-ui-library";
import { PRICE_RANGE_OPTIONS, type PriceRangeId } from "@dransay/api/models";
import { useTranslation } from "react-i18next";
import { ClickableCheckboxRow } from "../components/clickable-checkbox-row.component";
import { FilterHeader } from "../filter-header.component";
import { useFiltersContext } from "../filters.context";
import type { FiltersState } from "../types";

type PriceRanges = FiltersState["priceRanges"];

export function PriceFilter() {
	const { t } = useTranslation("product");
	const { filters, onFilterChange, facets } = useFiltersContext();
	const value = filters.priceRanges ?? [];
	const facetCounts = facets?.price;

	const getCount = (rangeId: PriceRangeId) => {
		// Return undefined only if no facet data at all (initial load without Typesense)
		if (!facetCounts?.length) return undefined;
		// Return 0 for missing values (filtered out by current selection)
		return facetCounts.find((f) => f.value === rangeId)?.count ?? 0;
	};

	const handleCheckboxChange = (rangeId: PriceRangeId, checked: boolean) => {
		let newValue: PriceRanges;
		if (checked) {
			newValue = [...(value || []), rangeId];
		} else {
			newValue = (value || []).filter((id: PriceRangeId) => id !== rangeId);
		}

		onFilterChange("priceRanges", newValue);
	};

	return (
		<Accordion.Item value="price" variant="filter" data-testid="price-filter">
			<Accordion.Trigger variant="filter" data-testid="price-filter-trigger">
				<FilterHeader
					title={t("filters.priceRanges")}
					value={Number(value?.length)}
					icon={<PriceIcon size={5} />}
				/>
			</Accordion.Trigger>
			<Accordion.Content variant="filter">
				<div className="space-y-3">
					{PRICE_RANGE_OPTIONS.map((priceRange) => {
						const isChecked = (value || []).includes(priceRange.id);
						const count = getCount(priceRange.id);
						// Disable if count is 0 and not already selected
						const isDisabled = count === 0 && !isChecked;

						return (
							<ClickableCheckboxRow
								key={priceRange.id}
								size="textSm"
								border="primary"
								checked={isChecked}
								disabled={isDisabled}
								onRowClick={() =>
									handleCheckboxChange(priceRange.id, !isChecked)
								}
								// @ts-ignore: dynamic key generation
								label={t(`filters.priceRanges.${priceRange.id}`)}
								count={count}
								data-testid={`price-filter-${priceRange.id}`}
								rowTestId={`price-filter-row-${priceRange.id}`}
								className="h-5 w-5"
							/>
						);
					})}
				</div>
			</Accordion.Content>
		</Accordion.Item>
	);
}
